import type { MailMessage, MailThread } from "@/lib/api";

function normEmail(value: string | undefined): string {
  return (value || "").trim().toLowerCase();
}

function labelIs(message: MailMessage, name: string): boolean {
  const target = name.toUpperCase();
  return (message.labelIds || []).some((label) => String(label).trim().toUpperCase() === target);
}

export function messageIsDraft(message: MailMessage): boolean {
  if (message.isDraft) return true;
  return labelIs(message, "DRAFT");
}

/**
 * A message we sent. Prefer the provider flag (Sent mailbox or SENT label).
 * Fall back to From matching this mailbox so a full thread still marks our
 * copies before the provider flag is present.
 */
export function messageIsSent(message: MailMessage, accountEmail?: string): boolean {
  if (messageIsDraft(message)) return false;
  if (message.sent === true) return true;
  if (labelIs(message, "SENT")) return true;
  const from = normEmail(message.fromEmail);
  const account = normEmail(accountEmail);
  return Boolean(from && account && from === account);
}

function rfcKey(message: MailMessage): string {
  const header = (message.messageIdHeader || "").trim().toLowerCase();
  return header ? `rfc:${header}` : "";
}

function sentRank(message: MailMessage, accountEmail?: string): number {
  if (message.sent === true || labelIs(message, "SENT")) return 2;
  if (messageIsSent(message, accountEmail)) return 1;
  return 0;
}

/** Drop a second copy of the same RFC message. Keep the sent copy when both exist. */
function dedupeMessages(messages: MailMessage[], accountEmail?: string): MailMessage[] {
  const ranked = [...messages].sort(
    (a, b) => sentRank(b, accountEmail) - sentRank(a, accountEmail),
  );
  const seenRfc = new Set<string>();
  const keep = new Set<MailMessage>();
  for (const message of ranked) {
    const key = rfcKey(message);
    if (key) {
      if (seenRfc.has(key)) continue;
      seenRfc.add(key);
    }
    keep.add(message);
  }
  return messages.filter((message) => keep.has(message));
}

/**
 * Inbox and Archive show the whole conversation, including mail we sent.
 * Sent shows only those sent copies. Drafts shows drafts.
 * A Sent listing that has not been flagged yet still renders: those rows
 * already came from the Sent mailbox.
 */
export function messagesForMailFolder(
  messages: MailMessage[],
  folder: string,
  accountEmail?: string,
): MailMessage[] {
  if (folder === "drafts") {
    const drafts = messages.filter((message) => messageIsDraft(message));
    return drafts.length ? drafts : messages;
  }
  const withoutDrafts = messages.filter((message) => !messageIsDraft(message));
  if (folder === "sent") {
    const sent = withoutDrafts.filter((message) => messageIsSent(message, accountEmail));
    const chosen = sent.length ? sent : withoutDrafts;
    return dedupeMessages(chosen, accountEmail);
  }
  return dedupeMessages(withoutDrafts, accountEmail);
}

export function threadAccountEmail(
  thread: { accountId?: string; accountEmail?: string },
  accounts: { id: string; email: string }[],
): string {
  const stamped = (thread.accountEmail || "").trim();
  if (stamped) return stamped;
  return accounts.find((account) => account.id === thread.accountId)?.email || "";
}

/**
 * Badge on the folder list.
 * Sent counts sent copies only, even when an older cache stored the full
 * conversation size (that showed "3" on a row that only had the sent mail).
 * Inbox counts the whole chain once it has been loaded.
 */
export function listChainCount(thread: MailThread, folder: string, accountEmail?: string): number {
  const messages = Array.isArray(thread.messages) ? thread.messages : [];
  const visible = messagesForMailFolder(messages, folder, accountEmail);
  if (folder === "sent" || folder === "drafts") {
    if (visible.length) return visible.length;
    if (!messages.length && typeof thread.messageCount === "number" && thread.messageCount > 0) {
      return thread.messageCount;
    }
    return Math.max(messages.length, 1);
  }
  if (thread.detailExpanded && visible.length) return visible.length;
  const declared =
    typeof thread.messageCount === "number" && thread.messageCount > 0 ? thread.messageCount : 0;
  return Math.max(declared, visible.length, messages.length, 1);
}

/** Inbox-only hint while the rest of the chain is still loading. Sent never uses this. */
export function inboxChainGap(
  thread: MailThread,
  folder: string,
  loadedCount: number,
): { showing: number; total: number } | null {
  if (folder === "sent" || folder === "drafts") return null;
  if (thread.detailExpanded && !thread.chainTruncated) return null;
  const declared = typeof thread.messageCount === "number" ? thread.messageCount : 0;
  if (declared > loadedCount) return { showing: loadedCount, total: declared };
  return null;
}
